// src/components/shared/toast/store.ts
//
// A headless toast store with an imperative API (FORM-0017).
//
// The imperative shape is the point: the calls that most need to report failure
// are not components. `DataContext.loadMeeting` used `alert()` precisely because
// it has no render tree to hang a banner off. `toast.error(...)` works from a
// context, a `catch` block, or an event handler with no hook and no prop
// drilling, which is what made it possible to delete every `alert()` call.
//
// Subscription is a plain callback set rather than a context value so the store
// can be imported directly. `<ToastViewport>` is the only subscriber.

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: number;
  variant: ToastVariant;
  message: string;
  /** Optional heading above the message. */
  title?: string;
  /** ms before auto-dismiss. 0 keeps it up until dismissed. */
  duration: number;
}

export interface ToastOptions {
  title?: string;
  duration?: number;
}

type Listener = (toasts: Toast[]) => void;

/** Errors stay up until dismissed; everything else clears itself. */
const DEFAULT_DURATION: Record<ToastVariant, number> = {
  success: 4000,
  info: 5000,
  warning: 7000,
  error: 0,
};

let toasts: Toast[] = [];
let nextId = 1;
const listeners = new Set<Listener>();
const timers = new Map<number, ReturnType<typeof setTimeout>>();

function emit(): void {
  // Hand out a copy so a subscriber holding the previous array still sees the
  // old value — React bails out of a re-render when the reference is unchanged.
  const snapshot = toasts;
  listeners.forEach((listener) => listener(snapshot));
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  listener(toasts);
  return () => {
    listeners.delete(listener);
  };
}

export function getToasts(): Toast[] {
  return toasts;
}

export function dismiss(id: number): void {
  const timer = timers.get(id);
  if (timer) {
    clearTimeout(timer);
    timers.delete(id);
  }
  const next = toasts.filter((t) => t.id !== id);
  if (next.length === toasts.length) return;
  toasts = next;
  emit();
}

export function dismissAll(): void {
  timers.forEach(clearTimeout);
  timers.clear();
  if (!toasts.length) return;
  toasts = [];
  emit();
}

function push(variant: ToastVariant, message: string, options: ToastOptions = {}): number {
  const id = nextId++;
  const duration = options.duration ?? DEFAULT_DURATION[variant];
  const entry: Toast = { id, variant, message, title: options.title, duration };

  // Newest first so the viewport can render top-down without reversing.
  toasts = [entry, ...toasts];
  emit();

  if (duration > 0) {
    timers.set(
      id,
      setTimeout(() => dismiss(id), duration),
    );
  }
  return id;
}

/**
 * Imperative toast API. Safe to call from anywhere, including outside React.
 * Returns the toast id so a caller can dismiss it early.
 */
export const toast = {
  success: (message: string, options?: ToastOptions) => push('success', message, options),
  error: (message: string, options?: ToastOptions) => push('error', message, options),
  info: (message: string, options?: ToastOptions) => push('info', message, options),
  warning: (message: string, options?: ToastOptions) => push('warning', message, options),
  dismiss,
  dismissAll,
};

export default toast;
